import { Modal, Spinner, Button, Form } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { setCloseInvoiceDataStoreModal, setCloseInvoiceUploadModal, setOpenInvoiceModal, setOpenInvoiceUploadModal, setShippingData } from "../../redux/features/invoice/invoiceSlice";
import { errorMessage } from "../../utlis/common";
import "./style.css";
import * as XLSX from "xlsx";
import { MESSAGES } from "../../utlis/messages";
import { useEffect, useState, useMemo, useCallback } from "react";
import { GetProfileDetails } from "../../redux/features/profile/profileActions";
import { Field, FormikProvider, useFormik } from "formik";
import FieldInputText from "../../modules/common/FieldInputText";
import SubmitButton from "../../modules/common/SubmitButton";
import * as Yup from 'yup';
import { storeInvoiceDocumentData } from "../../redux/features/invoice/invoiceAction";

const CURRENCY_OPTIONS = ['EUR', 'USD', 'GBP'];

const InvoiceDataStoreModal = () => {
    const dispatch: any = useDispatch();
    const [loading, setLoading] = useState(false);
    const { openInvoiceDataStoreModal, editInvoiceData, shippingData } = useSelector(
        (state: any) => state.invoiceSlice
    );
    const [profileDataLocal, setProfileDataLocal] = useState<any>(null);

    useEffect(() => {
        if (openInvoiceDataStoreModal && !profileDataLocal) {
            dispatch(GetProfileDetails())
                .then((res: any) => setProfileDataLocal(res?.payload?.data))
                .catch((err) => console.error("Failed to fetch profile:", err));
        }
    }, [openInvoiceDataStoreModal]);

    const handleClose = () => {
        dispatch(setCloseInvoiceDataStoreModal());
    };

    const tipo = profileDataLocal?.country_of_manufacturer || "tipo-1";
    const isTipo1 = tipo === "tipo-1";
    const isTipo2 = tipo === "tipo-2";
    const isTipo3 = tipo === "tipo-3";

    const isTipo1Only = isTipo1;
    const showTransportChoice = isTipo2;
    const isBoatOnly = isTipo3;
    const showAdvancedFields = isTipo2 || isTipo3;



    const getValidationSchema = useCallback(() => {
        let schema = Yup.object({
            invoice_date: Yup.date().required('Invoice date is required'),
            currency: Yup.string().required('Currency is required'),
            expected_delivery_date: Yup.date().required('Expected delivery date is required'),
            invoice_number: Yup.string().required('Invoice number is required'),
        });

        if (isTipo1Only) {
            schema = schema.concat(Yup.object({
                number_pallets: Yup.number().integer('Must be an integer').min(1, 'Must be at least 1').required('Number of pallets is required'),
            }));
        } else {
            schema = schema.concat(Yup.object({
                transport_type: Yup.string().required('Transport type is required'),
                number_boxes: Yup.number().integer('Must be an integer').min(0, 'Must be at least 0').required('Number of boxes is required'),
                gross_weight: Yup.number().min(0, 'Must be at least 0').required('Gross weight is required'),
                net_weight: Yup.number().min(0, 'Must be at least 0').required('Net weight is required'),
                number_pallets: Yup.number().integer('Must be an integer').min(1, 'Must be at least 1').required('Number of pallets is required'),
                bill_of_lading: Yup.string().when('transport_type', {
                    is: 'boat',
                    then: (schema) => schema.required('Bill of lading is required'),
                    otherwise: (schema) => schema.notRequired(),
                }),
                container_id: Yup.number().when('transport_type', {
                    is: 'boat',
                    then: (schema) => schema.min(1, 'Must be at least 1').required('Number of containers is required'),
                    otherwise: (schema) => schema.notRequired(),
                }),

            }));
        }

        schema = schema.concat(
            Yup.object({
                expected_delivery_date: Yup.date()
                    .min(
                        Yup.ref('invoice_date'),
                        'Invoice date cannot be after expected delivery date'
                    )
                    .required('Expected delivery date is required'),
            })
        );


        return schema;
    }, [isTipo1Only, isTipo2, isTipo3]);

    const initialValues = useMemo(() => {
        if (shippingData) {
            return {
                invoice_date: shippingData.invoice_date || "",
                currency: shippingData.currency || "EUR",
                expected_delivery_date: shippingData.expected_delivery_date || "",
                transport_type:
                    shippingData.transport_type ||
                    (isTipo1Only ? "truck" : isBoatOnly ? "boat" : "truck"),
                invoice_number: shippingData.invoice_number || "",
                number_boxes: shippingData.number_boxes || "",
                gross_weight: shippingData.gross_weight || "",
                net_weight: shippingData.net_weight || "",
                number_pallets: shippingData.number_pallets || "",
                bill_of_lading: shippingData.bill_of_lading || "",
                container_id: shippingData.container_id || "",
            };
        }

        return {
            invoice_date: "",
            currency: "EUR",
            expected_delivery_date: "",
            transport_type: isTipo1Only ? "truck" : isBoatOnly ? "boat" : "truck",
            invoice_number: "",
            number_boxes: "",
            gross_weight: "",
            net_weight: "",
            number_pallets: "",
            bill_of_lading: "",
            container_id: "",
        };
    }, [shippingData, isTipo1Only, isBoatOnly]);
    
    const formik = useFormik({
        enableReinitialize: true,
        initialValues,
        validationSchema: Yup.lazy(() => getValidationSchema()),
        onSubmit: async (values) => {
            try {
                setLoading(true);
                const bodyData = {
                    ...values,
                    invoice_id: editInvoiceData?.id, // Assume invoice_id from redux
                };
                const result = await dispatch(storeInvoiceDocumentData(bodyData)).unwrap();
                // Store the shipping data in state for later use in InvoiceModal
                dispatch(setShippingData(values));
                dispatch(setOpenInvoiceUploadModal({ ...bodyData }));
                handleClose();
            } catch (error) {
                console.error("Submission failed:", error);
                errorMessage('Failed to submit shipping data');
            } finally {
                setLoading(false);
            }
        },
    });

    const handleTransportChange = useCallback((type: string) => {
        formik.setFieldValue('transport_type', type);
        if (type === 'truck') {
            formik.setFieldValue('bill_of_lading', '');
        }
        // Re-validate
        formik.validateForm();
    }, [formik]);

    const isBoatSelected = formik.values.transport_type === 'boat';

    return (
        <Modal show={openInvoiceDataStoreModal} onHide={handleClose} centered size="lg">
            <Modal.Header className="invoice-header" closeButton>
                <Modal.Title>Select shipping method</Modal.Title>
            </Modal.Header>
            <Modal.Body className="">
                {loading ? (
                    <div className="d-flex flex-column align-items-center justify-content-center py-4">
                        <Spinner animation="border" role="status" />
                        <span className="mt-2">Loading...</span>
                    </div>
                ) : (
                    <FormikProvider value={formik}>
                        <form onSubmit={formik.handleSubmit} noValidate>
                            {/* Common fields */}
                            <div className="row mb-3">
                                <div className="col-md-6">
                                    <Field name="invoice_date" label="Invoice date (DATA BOLLA)" component={FieldInputText} type="date" required />
                                </div>
                                <div className="col-md-6">
                                    <Form.Group>
                                        <Form.Label>Currency *</Form.Label>
                                        <Form.Select
                                            name="currency"
                                            value={formik.values.currency}
                                            onChange={formik.handleChange}
                                            isInvalid={formik.touched.currency && !!formik.errors.currency}
                                        >
                                            {CURRENCY_OPTIONS.map((opt) => (
                                                <option key={opt} value={opt}>{opt}</option>
                                            ))}
                                        </Form.Select>
                                        <Form.Control.Feedback type="invalid">
                                            {formik.touched.currency && formik.errors.currency ? String(formik.errors.currency) : null}
                                        </Form.Control.Feedback>                                    </Form.Group>
                                </div>
                                <div className="col-md-12">
                                    <Field name="expected_delivery_date" label="Expected delivery date (DATA PREVISTO ARRIVO)" component={FieldInputText} type="date" required />
                                </div>
                            </div>

                            {/* Transport choice for tipo-2 */}
                            {showTransportChoice && (
                                <div className="mb-3">
                                    <label className="form-label">How is the shipment made?</label>
                                    <p className="text-muted small mb-2">
                                        Provide invoice date, currency and expected delivery date. If boat, also add the bill of lading number.
                                    </p>
                                    <div className="d-flex gap-2">
                                        <Button
                                            variant={formik.values.transport_type === 'truck' ? 'primary' : 'outline-primary'}
                                            onClick={() => handleTransportChange('truck')}
                                        >
                                            Truck
                                        </Button>
                                        <Button
                                            variant={formik.values.transport_type === 'boat' ? 'primary' : 'outline-primary'}
                                            onClick={() => handleTransportChange('boat')}
                                        >
                                            Boat
                                        </Button>
                                    </div>
                                </div>
                            )}

                            {/* Fixed transport info for tipo-1 and tipo-3 */}
                            {isTipo1Only && (
                                <div className="mb-3">
                                    <label className="form-label">Shipping method</label>
                                    <p className="text-muted small mb-2">Truck only: invoice number and pallets are required.</p>
                                </div>
                            )}
                            {isBoatOnly && (
                                <div className="mb-3">
                                    <label className="form-label">Shipping method</label>
                                    <p className="text-muted small mb-2">Boat only: fill all fields and add the bill of lading number.</p>
                                </div>
                            )}

                            {/* Tipo-1 fields */}
                            {isTipo1Only && (
                                <div className="row">
                                    <div className="col-md-6">
                                        <Field name="invoice_number" label="Invoice number (NUMERO BOLLA)" placeholder="e.g., 33" component={FieldInputText} required />
                                    </div>
                                    <div className="col-md-6">
                                        <Field name="number_pallets" label="Number of pallets *" placeholder="e.g., 3" component={FieldInputText} type="number" required />
                                    </div>
                                </div>
                            )}

                            {/* Advanced fields for tipo-2 and tipo-3 */}
                            {showAdvancedFields && (
                                <div className="row">
                                    <div className="col-md-6">
                                        <Field name="number_boxes" label="Number of boxes *" placeholder="e.g., 12" component={FieldInputText} type="number" required />
                                    </div>
                                    <div className="col-md-6">
                                        <Field name="invoice_number" label="Invoice number *" placeholder="e.g., 33" component={FieldInputText} required />
                                    </div>
                                    <div className="col-md-6">
                                        <Field name="gross_weight" label="Total gross weight (kg) *" placeholder="e.g., 245.6" component={FieldInputText} type="number" step="0.1" required />
                                    </div>
                                    <div className="col-md-6">
                                        <Field name="net_weight" label="Total net weight (kg) *" placeholder="e.g., 220.4" component={FieldInputText} type="number" step="0.1" required />
                                    </div>
                                    <div className="col-md-6">
                                        <Field name="number_pallets" label="Number of pallets *" placeholder="e.g., 3" component={FieldInputText} type="number" required />
                                    </div>
                                    {(isBoatSelected || isBoatOnly) && (
                                        <div className="col-md-6">
                                            <Field name="bill_of_lading" label="Bill of lading *" placeholder="e.g., BL987654" component={FieldInputText} required />
                                        </div>
                                    )}
                                    {(isBoatSelected || isBoatOnly) && (
                                        <div className="col-md-6">
                                            <Field
                                                name="container_id"
                                                label="Number of containers *"
                                                placeholder="e.g., 2"
                                                component={FieldInputText}
                                                type="number"
                                                required
                                            />
                                        </div>
                                    )}

                                </div>
                            )}

                            <div className="d-grid mt-3">
                                <SubmitButton title="Proceed" type="submit" loading={loading} />
                            </div>

                            <p className="text-muted small mt-2">
                                * Required fields are marked with *. Ensure correctness before proceeding.
                            </p>
                        </form>
                    </FormikProvider>
                )}
            </Modal.Body>
        </Modal>
    );
};

export default InvoiceDataStoreModal;
